import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Real self-serve hotel signup. Distinct from `/signup` (demo lead capture) —
 * this creates a live account, a wizard draft, and drops the user into the
 * onboarding flow.
 */
export default function HotelSignup() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [form, setForm] = useState({
        hotel_name: "", admin_name: "", email: "", password: "",
        country: "Albania", phone: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) {
            toast.error("Fjalëkalimi duhet të ketë të paktën 8 karaktere");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/hotels/signup", form);
            // Store token + let AuthContext hydrate.
            localStorage.setItem("akm_token", data.access_token);
            await refresh();
            toast.success("Llogaria u krijua! Le të konfigurojmë hotelin tuaj.");
            navigate(`/wizard/${data.draft_id}`, { replace: true });
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Krijimi dështoi");
        } finally {
            setBusy(false);
        }
    };

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div className="min-h-screen w-full bg-white text-slate-900 antialiased" data-testid="hotel-signup-page">
            {/* Nav */}
            <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5">
                <Link to="/" className="flex items-center gap-2 text-[18px] font-bold tracking-tight">
                    <span className="text-[#F26322]">akomoda</span>
                </Link>
                <div className="flex items-center gap-4 text-[13px]">
                    <span className="hidden text-slate-500 sm:inline">Keni llogari?</span>
                    <Link to="/login" className="rounded-xl border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 transition hover:border-[#F26322] hover:text-[#F26322]">
                        Hyni
                    </Link>
                </div>
            </nav>

            <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 pb-16 pt-2 lg:grid-cols-[minmax(0,1fr),580px]">
                {/* Left — pitch */}
                <div className="hidden lg:flex lg:flex-col lg:pt-6">
                    <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#F26322]">
                        <Sparkles size={11} /> 14 ditë falas · pa kartë
                    </div>
                    <h1 className="mt-4 text-[46px] font-black leading-[1.05] tracking-[-0.02em] text-slate-900">
                        Nga rregjistrimi tek<br />
                        rezervimi i parë <span className="text-[#F26322]">në 10 minuta.</span>
                    </h1>
                    <p className="mt-5 max-w-[520px] text-[15px] leading-relaxed text-slate-600">
                        PMS, Channel Manager, POS dhe Booking Engine — të gjitha të lidhura. Filloni provën
                        falas 14-ditore, pa kartë krediti.
                    </p>
                    <ul className="mt-6 space-y-3 text-[14px] text-slate-700">
                        {[
                            "Konfigurim me hap-pas-hapi wizard",
                            "Booking Engine i personalizueshëm i lidhur me faqen tuaj",
                            "Sinkronizim me Booking.com, Airbnb, Expedia përmes Channex",
                            "Fatura elektronike + POS për restorant/bar",
                        ].map((t) => (
                            <li key={t} className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="shrink-0 text-emerald-500" />
                                <span>{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Right — form card */}
                <form onSubmit={submit} className="w-full self-start rounded-2xl border border-slate-200 bg-white p-7 shadow-sm" data-testid="hotel-signup-form">
                    <h2 className="text-[22px] font-bold tracking-tight text-slate-900">Krijo llogarinë e hotelit</h2>
                    <p className="mt-1 text-[13px] text-slate-500">Fillo në 30 sekonda — konfigurimin e mbarojmë së bashku.</p>

                    <div className="mt-5 space-y-4">
                        <Fld label="Emri i hotelit" req>
                            <input required minLength={2} value={form.hotel_name} onChange={update("hotel_name")} className="wsi-input" data-testid="signup-hotel-name" placeholder="Villa Egnatia" />
                        </Fld>

                        <Fld label="Emri juaj" req>
                            <input required minLength={2} value={form.admin_name} onChange={update("admin_name")} className="wsi-input" data-testid="signup-admin-name" placeholder="Enri Bregu" />
                        </Fld>

                        <div className="grid grid-cols-2 gap-3">
                            <Fld label="Shteti" req>
                                <select value={form.country} onChange={update("country")} className="wsi-input">
                                    {["Albania","Kosovo","North Macedonia","Montenegro","Greece","Italy","Germany","Other"].map((c) =>
                                        <option key={c} value={c}>{c}</option>)}
                                </select>
                            </Fld>
                            <Fld label="Telefoni">
                                <input value={form.phone} onChange={update("phone")} className="wsi-input" placeholder="+355 …" />
                            </Fld>
                        </div>

                        <Fld label="Email" req>
                            <input required type="email" value={form.email} onChange={update("email")} className="wsi-input" data-testid="signup-email" placeholder="you@hotel.com" />
                        </Fld>

                        <Fld label="Fjalëkalimi (min 8)" req>
                            <input required minLength={8} type="password" value={form.password} onChange={update("password")} className="wsi-input" data-testid="signup-password" placeholder="••••••••" />
                        </Fld>
                    </div>

                    <button
                        type="submit"
                        disabled={busy}
                        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#F26322] px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#e0561b] disabled:opacity-60"
                        data-testid="signup-submit"
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                        {busy ? "DUKE KRIJUAR…" : "KRIJO LLOGARINË"}
                    </button>

                    <div className="mt-4 text-center text-[11.5px] text-slate-500">
                        Duke krijuar llogari, ju pranoni{" "}
                        <Link to="/terms" className="underline hover:text-[#F26322]">kushtet</Link> dhe{" "}
                        <Link to="/privacy" className="underline hover:text-[#F26322]">politikën e privatësisë</Link>.
                    </div>
                </form>
            </div>

            <style>{`
                .wsi-input { width:100%; padding:0.65rem 0.85rem; border-radius:0.75rem; border:1px solid #e2e8f0; background:#fff; color:#0f172a; outline:none; font-size:14px; transition:border-color .15s, box-shadow .15s; }
                .wsi-input:focus { border-color:#F26322; box-shadow:0 0 0 3px rgba(242,99,34,0.12); }
                .wsi-input::placeholder { color:#94a3b8; }
            `}</style>
        </div>
    );
}

function Fld({ label, children, req }) {
    return (
        <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-slate-700">
                {label}{req && <span className="text-[#F26322]">*</span>}
            </span>
            {children}
        </label>
    );
}
